/**
 * UserUsageModal — full Usage Analytics scoped to a single user.
 * Opens as a slide-over drawer from the right when you click a user in Users.js.
 * Shows all 13 charts from LLMStats, filtered to that user's source_id only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from 'recharts';
import { X, Loader2, User, TrendingUp, Activity, Zap, Clock, Layers, SlidersHorizontal } from 'lucide-react';
import {
  getLLMStats, getLLMHeatmap, getDailyByProvider,
  getLatencyHistogram, getUsageBreakdown, getUsageFilters, getUserUsageAdmin,
} from '../api';
import { useTheme } from '../contexts/ThemeContext';
import { TokenValue } from '../components/TokenValue';
import FilterDrawer from '../components/FilterDrawer';

// ── constants ────────────────────────────────────────────────────────────────
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PROVIDER_COLORS = [
  '#f97316', '#3b82f6', '#a855f7', '#22c55e', '#ec4899',
  '#eab308', '#14b8a6', '#ef4444',
];
const PRESETS = [
  // `label` = short (trigger), `long` = descriptive (dropdown list).
  { label: 'Today',    long: 'Today',        days: '1'   },
  { label: '7d',       long: 'Past 7 days',  days: '7'   },
  { label: '30d',      long: 'Past 30 days', days: '30'  },
  { label: '90d',      long: 'Past 90 days', days: '90'  },
  { label: 'All time', long: 'All time',     days: 'all' },
];
const WINDOW_LABELS = { min: 'Per minute', hour: 'Per hour', '6h': 'Per 6 hours', day: 'Per day' };

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(n)   { return (n || 0).toLocaleString(); }
function fmtM(n)  { return ((n || 0) / 1e6).toFixed(2) + 'M'; }
function fmtUSD(n){ return '$' + (n || 0).toFixed(4); }
// `daily`/`dailyProv` collapse to month buckets ('YYYY-MM') once a range has
// too many days (see _collapse_daily in llm_stats.py); day/week keys stay
// 'YYYY-MM-DD'. Hardcoded month names, not toLocaleDateString — Intl's
// 'short' month for September is locale-dependent ("Sep" vs "Sept").
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDailyId(id) {
  if (!id) return id;
  if (/^\d{4}-\d{2}$/.test(id)) {
    const [y, m] = id.split('-');
    return `${MONTH_ABBR[Number(m) - 1]} ${y.slice(2)}`;
  }
  return id.slice(5);
}
// Explicit tick spacing instead of Recharts' width-based default, which skips
// a different number of labels per chart size. The +2 slack avoids jumping
// from "show all" straight to "every 2nd" when count is barely over target.
function tickInterval(n, target = 7) { return n <= target + 2 ? 0 : Math.max(0, Math.ceil(n / target) - 1); }

// ── Heatmap ───────────────────────────────────────────────────────────────────
function Heatmap({ cells, maxRequests }) {
  const [hover, setHover] = useState(null);
  const grid = {};
  (cells || []).forEach(c => { grid[`${c.dow}-${c.hour}`] = c; });
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const dayTotals = DOW.map((_, d) => {
    let requests = 0, cost = 0;
    hours.forEach(h => { const c = grid[`${d}-${h}`]; if (c) { requests += c.requests; cost += c.cost; } });
    return { requests, cost };
  });

  return (
    <div>
      <div className="mb-2 min-h-[1.25rem] text-xs">
        {hover
          ? <span className="text-fg">
              {hover.label}{hover.hour != null ? ` ${hover.hour}:00 IST` : ' · all day'} —{' '}
              <span className="text-orange font-semibold">{fmt(hover.requests)}</span> req ·{' '}
              <span className="text-green-400">{fmtUSD(hover.cost)}</span>
            </span>
          : <span className="text-fg-muted">Hover a cell or day label</span>
        }
      </div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex">
            <div className="w-9" />
            {hours.map(h => (
              <div key={h} className="w-5 text-center text-[9px] text-fg-muted">
                {h % 4 === 0 ? h : ''}
              </div>
            ))}
          </div>
          {DOW.map((label, d) => (
            <div key={d} className="flex items-center">
              <div
                className="w-9 text-[10px] text-fg-muted pr-1 text-right cursor-pointer hover:text-fg"
                onMouseEnter={() => setHover({ label, hour: null, requests: dayTotals[d].requests, cost: dayTotals[d].cost })}
                onMouseLeave={() => setHover(null)}
              >{label}</div>
              {hours.map(h => {
                const cell = grid[`${d}-${h}`];
                const n    = cell ? cell.requests : 0;
                const intensity = maxRequests ? Math.min(1, n / maxRequests) : 0;
                const bg = n === 0
                  ? 'rgba(128,128,128,0.1)'
                  : `rgba(249,115,22,${0.12 + intensity * 0.88})`;
                const active = hover?.label === label && hover?.hour === h;
                return (
                  <div
                    key={h}
                    className={`w-5 h-5 m-[1px] rounded-sm cursor-pointer ${active ? 'ring-1 ring-white' : ''}`}
                    onMouseEnter={() => setHover({ label, hour: h, requests: n, cost: cell?.cost || 0 })}
                    onMouseLeave={() => setHover(null)}
                    style={{ backgroundColor: bg }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-surface rounded-xl border border-panel-border p-4">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className={`w-3.5 h-3.5 ${color}`} />}
        <p className="text-fg-muted text-xs">{label}</p>
      </div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

// ── UsageBar (rate-limit / budget bars — same UI as My Usage) ──────────────────
function UsageBar({ label, used, limit, unit = '' }) {
  const unlimited = !limit || limit <= 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-orange';
  const textColor = pct >= 90 ? 'text-red-400' : pct >= 70 ? 'text-yellow-400' : 'text-fg-muted';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-fg-muted">{label}</span>
        <span className={`font-medium ${textColor}`}>
          {used}{unit} / {unlimited ? '∞' : `${limit}${unit}`}
          {!unlimited && <span className="ml-1 opacity-60">({pct}%)</span>}
        </span>
      </div>
      <div className="w-full h-1.5 bg-field border border-field-border rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${unlimited ? 'bg-fg/10 w-full' : barColor}`}
          style={unlimited ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
      <h3 className="text-sm font-semibold text-fg mb-4">{title}</h3>
      {children}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function UserUsageModal({ user, onClose }) {
  const { theme } = useTheme();

  const axisColor    = theme === 'light' ? '#6b7280' : '#9aa0aa';
  const tooltipStyle = {
    background:   theme === 'light' ? '#ffffff' : '#141414',
    border:       `1px solid ${theme === 'light' ? '#e5e7eb' : '#2a2a2a'}`,
    borderRadius: '8px',
    color:        theme === 'light' ? '#111827' : '#f5f5f5',
  };
  const tooltipLabelStyle = { color: theme === 'light' ? '#111827' : '#f5f5f5' };

  // Preset sets days; custom range sets startDate/endDate. Default: 7d.
  const [dateFilter, setDateFilter] = useState({ days: '7', startDate: null, endDate: null });
  const [model, setModel] = useState('');
  const [product, setProduct] = useState('');
  // '' = personal (the default); else the team's project_id. Enforcement is
  // always exactly one context at a time, so everything below — analytics,
  // tier, and the live limit bars — scopes together to whichever is picked,
  // never a combined personal-plus-every-team total.
  const [teamId, setTeamId] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);

  // Filter drawer — the drawer itself is the shared FilterDrawer component (one user,
  // so no User filter here).
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading]     = useState(true);

  // data buckets
  const [overall, setOverall]           = useState({});
  const [byModel, setByModel]           = useState([]);
  const [byProvider, setByProvider]     = useState([]);
  const [daily, setDaily]               = useState([]);
  const [heatmap, setHeatmap]           = useState({ cells: [], max_requests: 0 });
  const [dailyProv, setDailyProv]       = useState({ rows: [], providers: [] });
  const [latency, setLatency]           = useState([]);
  const [explorerRows, setExplorerRows] = useState([]);

  // Rate-limit / budget bars — live counters + tier caps. Independent of the
  // analytics date/model/product filters (refetched only on user or context
  // change, not on every filter change) since they reflect current standing,
  // not a filtered history.
  const [rlUsage, setRlUsage]                 = useState([]);
  const [budgetUsage, setBudgetUsage]         = useState([]);
  const [budgetPerModel, setBudgetPerModel]   = useState([]);
  // Depleting credit balances — distinct from the rolling-window budget above.
  // Read-only here: both scopes are edited from Users.js (the org pool card,
  // and this member's sub-cap via the "Set credit limit" row-menu action).
  const [credits, setCredits]                 = useState({ org: null, member: null });

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const uid = user.user_id;
      const custom = dateFilter.startDate && dateFilter.endDate;
      const days = custom ? null : (dateFilter.days === 'all' ? null : parseInt(dateFilter.days));
      const sd = custom ? dateFilter.startDate : null;
      const ed = custom ? dateFilter.endDate : null;

      const m = model || null;
      const p = product || null;
      const tid = teamId || null;
      const [statsR, heatR, provR, latR, exR] = await Promise.all([
        getLLMStats(days, m, sd, ed, uid, p, tid),
        getLLMHeatmap(days, m, sd, ed, uid, p, tid),
        getDailyByProvider(days, sd, ed, uid, p, tid),
        getLatencyHistogram(days, m, sd, ed, uid, p, tid),
        // explorer: breakdown by model for this user
        getUsageBreakdown({ days: days || undefined, start_date: sd || undefined, end_date: ed || undefined, user_id: uid, model: m || undefined, product: p || undefined, project_id: tid || undefined, by: 'model', limit: 20 }),
      ]);

      setOverall(statsR.data.overall || {});
      setByModel(statsR.data.by_model || []);
      setByProvider(statsR.data.by_provider || []);
      setDaily(statsR.data.daily || []);
      setHeatmap({ cells: heatR.data.cells || [], max_requests: heatR.data.max_requests || 0 });

      // reshape daily-by-provider
      const provRows    = provR.data.rows || [];
      const providerSet = [...new Set(provRows.map(r => r.provider))];
      const dateMap     = {};
      provRows.forEach(r => {
        if (!dateMap[r.date]) dateMap[r.date] = { date: r.date };
        dateMap[r.date][r.provider] = r.cost;
      });
      setDailyProv({ rows: Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)), providers: providerSet });

      setLatency(latR.data.buckets || []);
      setExplorerRows(exR.data.rows || []);
    } catch (e) {
      console.error('UserUsageModal load error', e);
    } finally {
      setLoading(false);
    }
  }, [user, dateFilter, model, product, teamId]);

  useEffect(() => { load(); }, [load]);

  // Model + product lists for the filter dropdowns.
  useEffect(() => {
    getUsageFilters().then(r => {
      setAvailableModels(r.data.models || []);
      setAvailableProducts(r.data.products || []);
    }).catch(() => {});
  }, []);

  // Rate-limit / budget usage for this user (live counters + tier caps), scoped
  // to the same selected context as everything else above — refetched on
  // every context switch, unlike the date/model/product filters it doesn't
  // otherwise depend on.
  useEffect(() => {
    if (!user) return;
    getUserUsageAdmin(user.user_id, null, teamId || null).then(r => {
      setAvailableTeams(r.data.available_teams || []);
      setRlUsage(r.data.rate_limit_usage || []);
      setBudgetUsage(r.data.budget_usage || []);
      setBudgetPerModel(r.data.budget_usage_per_model || []);
      setCredits(r.data.credits || { org: null, member: null });
    }).catch(() => {});
  }, [user, teamId]);

  const activeFilterCount =
    (model ? 1 : 0) +
    (product ? 1 : 0) +
    (teamId ? 1 : 0) +
    ((dateFilter.startDate && dateFilter.endDate) || dateFilter.days !== '7' ? 1 : 0);

  // Escape closes the modal — but only when the filter drawer isn't open (the drawer
  // owns its own Escape-to-close via FilterDrawer).
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape' && !filterOpen) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, filterOpen]);

  if (!user) return null;

  const avgCost    = overall.total_requests > 0 ? overall.total_cost / overall.total_requests : 0;
  // Writes are the misses — see the same computation in LLMStats.js.
  const cacheLookups = (overall.total_cache_read_tokens || 0) + (overall.total_cache_write_tokens || 0);
  const cacheHit   = cacheLookups > 0
    ? (overall.total_cache_read_tokens / cacheLookups) * 100
    : 0;
  // Avg cost per request, split by token type (backend derives type cost from pricing).
  const reqs = overall.total_requests || 0;
  const avgInputCost      = reqs ? (overall.total_input_cost || 0) / reqs : 0;
  const avgOutputCost     = reqs ? (overall.total_output_cost || 0) / reqs : 0;
  const avgCacheReadCost  = reqs ? (overall.total_cache_read_cost || 0) / reqs : 0;
  const avgCacheWriteCost = reqs ? (overall.total_cache_write_cost || 0) / reqs : 0;
  const maxModelCost = Math.max(...byModel.map(m => m.cost), 0.000001);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-6xl bg-surface border-l border-panel-border z-50 flex flex-col shadow-2xl">

        {/* Header — time-frame block wraps to its own line, then chips wrap; close is absolute. */}
        <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2 pl-6 pr-14 py-4 border-b border-panel-border shrink-0">
          <div className="flex items-center gap-3 min-w-0 mr-auto">
            <div className="w-9 h-9 rounded-full bg-orange/10 border border-orange/20 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-orange" />
            </div>
            <div className="min-w-0">
              <p className="text-fg font-semibold truncate">{user.user_email_id}</p>
              <p className="text-fg-muted text-xs truncate">{user.user_name || 'Usage Analytics'}</p>
            </div>
          </div>

          {/* Filters button — opens the draft filter panel (Time frame + Model) */}
          <button
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-2 mr-3 px-3 py-1.5 bg-field border border-field-border hover:border-orange/50 text-fg rounded-lg text-xs font-medium transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-orange text-white text-xs font-semibold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button onClick={onClose} className="absolute top-3.5 right-4 p-1.5 text-fg-muted hover:text-fg rounded-lg hover:bg-field transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {loading ? (
            <div className="flex items-center justify-center h-64 gap-3 text-fg-muted">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading analytics…
            </div>
          ) : (

            <>
              {/* 10 stat cards (6 totals on row 1, per-type cost on row 2) */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                <StatCard label="Total Cost"    value={`$${(overall.total_cost || 0).toFixed(2)}`}   color="text-green-400"  icon={TrendingUp} />
                <StatCard label="Requests"      value={fmt(overall.total_requests)}                  color="text-orange"     icon={Activity}   />
                <StatCard label="Input Tokens"  value={fmtM(overall.total_input_tokens)}             color="text-blue-400"   icon={Layers}     />
                <StatCard label="Output Tokens" value={fmtM(overall.total_output_tokens)}            color="text-purple-400" icon={Zap}        />
                <StatCard label="Avg Cost/Req"  value={`$${avgCost.toFixed(4)}`}                     color="text-yellow-400" icon={TrendingUp} />
                <StatCard label="Cache Hit"     value={`${cacheHit.toFixed(1)}%`}                    color="text-teal-400"   icon={Clock}      />
                <StatCard label="Avg Input Cost/Req"       value={`$${avgInputCost.toFixed(4)}`}      color="text-blue-400"   icon={Layers}     />
                <StatCard label="Avg Output Cost/Req"      value={`$${avgOutputCost.toFixed(4)}`}     color="text-purple-400" icon={Zap}        />
                <StatCard label="Avg Cache-Read Cost/Req"  value={`$${avgCacheReadCost.toFixed(4)}`}  color="text-green-400"  icon={Clock}      />
                <StatCard label="Avg Cache-Write Cost/Req" value={`$${avgCacheWriteCost.toFixed(4)}`} color="text-yellow-400" icon={TrendingUp} />
              </div>

              {/* Token breakdown */}
              <Section title="Token breakdown (cost drivers)">
                {(() => {
                  const parts = [
                    { k: 'Input',       v: overall.total_input_tokens        || 0, c: '#3b82f6' },
                    { k: 'Output',      v: overall.total_output_tokens       || 0, c: '#a855f7' },
                    { k: 'Cache read',  v: overall.total_cache_read_tokens   || 0, c: '#22c55e' },
                    { k: 'Cache write', v: overall.total_cache_write_tokens  || 0, c: '#eab308' },
                    { k: 'Reasoning',   v: overall.total_reasoning_tokens    || 0, c: '#ec4899' },
                  ];
                  const total = parts.reduce((s, p) => s + p.v, 0) || 1;
                  return (
                    <>
                      <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-fg/5">
                        {parts.map(p => p.v > 0 && (
                          <div key={p.k}
                            style={{ width: `${(p.v / total) * 100}%`, backgroundColor: p.c }}
                            title={`${p.k}: ${fmt(p.v)}`}
                          />
                        ))}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        {parts.map(p => (
                          <div key={p.k}>
                            <div className="flex items-center gap-1.5 text-xs text-fg-muted">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.c }} />
                              {p.k}
                            </div>
                            <div className="text-fg text-sm font-semibold mt-0.5">{fmtM(p.v)}</div>
                            <div className="text-[11px] text-fg-muted">{((p.v / total) * 100).toFixed(1)}%</div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </Section>

              {/* Requests heatmap */}
              <Section title="Request heatmap">
                <Heatmap cells={heatmap.cells} maxRequests={heatmap.max_requests} />
              </Section>

              {/* Usage Explorer — by model */}
              {explorerRows.length > 0 && (
                <Section title="Usage by model">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-fg-muted border-b border-panel-border">
                          <th className="py-2 pr-3 font-medium">Model</th>
                          <th className="py-2 pr-3 font-medium text-right">Requests</th>
                          <th className="py-2 pr-3 font-medium text-right">Cost</th>
                          <th className="py-2 pr-3 font-medium text-right">Input tok</th>
                          <th className="py-2 pr-3 font-medium text-right">Output tok</th>
                          <th className="py-2 pr-3 font-medium text-right">Cache read tok</th>
                          <th className="py-2 font-medium text-right">Cache write tok</th>
                        </tr>
                      </thead>
                      <tbody>
                        {explorerRows.map(r => (
                          <tr key={r.key} className="border-b border-panel-border/50">
                            <td className="py-2 pr-3">
                              <div className="text-fg truncate max-w-[220px]" title={r.label}>{r.label}</div>
                              <div className="h-1 mt-1 rounded-full bg-orange/70"
                                style={{ width: `${(r.cost / maxModelCost) * 100}%`, maxWidth: '200px' }} />
                            </td>
                            <td className="py-2 pr-3 text-right text-fg"><TokenValue value={r.requests} unit="requests" /></td>
                            <td className="py-2 pr-3 text-right text-green-400"><TokenValue value={r.cost} currency /></td>
                            <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.input_tokens} /></td>
                            <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.output_tokens} /></td>
                            <td className="py-2 pr-3 text-right text-fg-muted"><TokenValue value={r.cache_read_tokens} /></td>
                            <td className="py-2 text-right text-fg-muted"><TokenValue value={r.cache_write_tokens} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              {/* Daily Cost + Daily Requests */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                <div className="bg-panel border border-panel-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-fg mb-3">Daily Cost</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={daily} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                        <XAxis dataKey="date" stroke={axisColor} fontSize={11} tickFormatter={formatDailyId} interval={tickInterval(daily.length)} />
                        <YAxis stroke={axisColor} fontSize={11} tickFormatter={v => `$${v}`} />
                        <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={formatDailyId} formatter={v => [`$${v.toFixed(4)}`, 'Cost']} />
                        <Line type="monotone" dataKey="cost" stroke="#f97316" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-panel border border-panel-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-fg mb-3">Daily Requests</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={daily} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                        <XAxis dataKey="date" stroke={axisColor} fontSize={11} tickFormatter={formatDailyId} interval={tickInterval(daily.length)} />
                        <YAxis stroke={axisColor} fontSize={11} />
                        <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={formatDailyId} formatter={v => [fmt(v), 'Requests']} />
                        <Line type="monotone" dataKey="requests" stroke="#3b82f6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Cost Trend by Provider */}
              {dailyProv.rows.length > 0 && (
                <Section title="Cost trend by provider">
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dailyProv.rows} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                        <XAxis dataKey="date" stroke={axisColor} fontSize={11} tickFormatter={formatDailyId} interval={tickInterval(dailyProv.rows.length, 12)} />
                        <YAxis stroke={axisColor} fontSize={11} tickFormatter={v => `$${v}`} />
                        <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} labelFormatter={formatDailyId} formatter={v => [`$${Number(v).toFixed(4)}`]} />
                        <Legend wrapperStyle={{ fontSize: '11px', color: axisColor }} />
                        {dailyProv.providers.map((p, i) => (
                          <Line key={p} type="monotone" dataKey={p}
                            stroke={PROVIDER_COLORS[i % PROVIDER_COLORS.length]}
                            strokeWidth={2} dot={false} connectNulls />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Section>
              )}

              {/* Cost by Model + Requests by Model */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                <div className="bg-panel border border-panel-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-fg mb-3">Cost by model</h3>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={byModel.slice(0, 8)} layout="vertical">
                        <XAxis type="number" stroke={axisColor} fontSize={11} tickFormatter={v => `$${v}`} />
                        <YAxis type="category" dataKey="model" stroke={axisColor} fontSize={9} width={110} />
                        <Tooltip contentStyle={tooltipStyle} formatter={v => [`$${v.toFixed(4)}`, 'Cost']} />
                        <Bar dataKey="cost" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-panel border border-panel-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-fg mb-3">Requests by model</h3>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={byModel.slice(0, 8)} layout="vertical">
                        <XAxis type="number" stroke={axisColor} fontSize={11} />
                        <YAxis type="category" dataKey="model" stroke={axisColor} fontSize={9} width={110} />
                        <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), 'Requests']} />
                        <Bar dataKey="requests" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Avg Latency by Model + Latency Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                <div className="bg-panel border border-panel-border rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-fg mb-3">Avg latency by model</h3>
                  <div className="space-y-3 max-h-52 overflow-y-auto">
                    {byModel.slice(0, 10).map((item, i) => {
                      const lat = item.avg_latency_ms || 0;
                      const maxLat = Math.max(...byModel.map(m => m.avg_latency_ms || 0), 1);
                      return (
                        <div key={i}>
                          <div className="flex justify-between mb-0.5">
                            <span className="text-fg text-xs truncate max-w-[130px]" title={item.model}>{item.model}</span>
                            <span className={`text-xs font-medium ${lat > 5000 ? 'text-red-400' : lat > 2000 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {lat > 1000 ? `${(lat / 1000).toFixed(1)}s` : `${Math.round(lat)}ms`}
                            </span>
                          </div>
                          <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${lat > 5000 ? 'bg-red-500' : lat > 2000 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${maxLat > 0 ? (lat / maxLat) * 100 : 0}%` }} />
                          </div>
                          <p className="text-fg-muted text-[10px] mt-0.5">{fmt(item.requests)} requests</p>
                        </div>
                      );
                    })}
                    {byModel.length === 0 && <p className="text-fg-muted text-xs">No latency data.</p>}
                  </div>
                </div>

                {latency.length > 0 && (
                  <div className="bg-panel border border-panel-border rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-fg mb-3">Latency distribution</h3>
                    <div className="h-52">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={latency}>
                          <XAxis dataKey="label" stroke={axisColor} fontSize={10} />
                          <YAxis stroke={axisColor} fontSize={11} />
                          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} formatter={v => [fmt(v), 'Requests']} />
                          <Bar dataKey="count" fill="#a855f7" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              {/* Cost by Provider */}
              {byProvider.length > 0 && (
                <Section title="Cost by provider">
                  <div className="space-y-3">
                    {byProvider.map((item, i) => (
                      <div key={i}>
                        <div className="flex justify-between mb-0.5">
                          <span className="text-fg text-sm">{item.provider}</span>
                          <span className="text-green-400 text-sm">{fmtUSD(item.cost)}</span>
                        </div>
                        <div className="h-2 bg-surface rounded-full overflow-hidden">
                          <div className="h-full bg-orange rounded-full"
                            style={{ width: `${(item.cost / (overall.total_cost || 1)) * 100}%` }} />
                        </div>
                        <p className="text-fg-muted text-xs mt-0.5">{fmt(item.requests)} requests</p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Rate limit usage (per model) */}
              <Section title="Rate limit usage (per model)">
                {rlUsage.length === 0 ? (
                  <p className="text-fg-muted text-sm">No models / rate limits configured.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {rlUsage.map((entry) => (
                      <div key={entry.model}>
                        <p className="text-xs font-mono font-medium text-fg mb-2.5">{entry.model}</p>
                        <div className="space-y-2.5">
                          {(entry.windows || []).map((w) => (
                            <UsageBar
                              key={w.window}
                              label={WINDOW_LABELS[w.window] || w.window}
                              used={w.used}
                              limit={w.limit}
                              unit=" req"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Budget usage (USD) */}
              <Section title="Budget usage (USD)">
                {budgetUsage.length === 0 ? (
                  <p className="text-fg-muted text-sm">No spend budget is set on this user's tier — usage is unlimited (cost-wise).</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {budgetUsage.map((w) => (
                      <UsageBar
                        key={w.window}
                        label={WINDOW_LABELS[w.window] || w.window}
                        used={w.spent.toFixed(4)}
                        limit={w.limit ? w.limit.toFixed(2) : 0}
                      />
                    ))}
                  </div>
                )}
              </Section>

              {/* Budget usage (per model) */}
              <Section title="Budget usage (per model)">
                {budgetPerModel.length === 0 ? (
                  <p className="text-fg-muted text-sm">No per-model spend cap is set on this user's tier.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {budgetPerModel.map((entry) => (
                      <div key={entry.model}>
                        <p className="text-xs font-mono font-medium text-fg mb-2.5">{entry.model}</p>
                        <div className="space-y-2.5">
                          {(entry.windows || []).map((w) => (
                            <UsageBar
                              key={w.window}
                              label={WINDOW_LABELS[w.window] || w.window}
                              used={w.spent.toFixed(4)}
                              limit={w.limit ? w.limit.toFixed(2) : 0}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Credits — depleting balance, distinct from the rolling-window budget
                  above. This member's sub-cap WITHIN this org — edited via the
                  "Set credit limit" row-menu action on /users. The org-wide pool
                  isn't specific to any one member, so it isn't shown here — it's
                  shown (and edited) in exactly one place: the Credits card on /users. */}
              {credits.member && (
                <Section title="Credits">
                  <UsageBar label="Credit limit" used={(credits.member.credit_limit - credits.member.credits_remaining).toFixed(2)} limit={credits.member.credit_limit.toFixed(2)} unit=" USD" />
                </Section>
              )}

              {/* Empty state */}
              {!loading && overall.total_requests === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Activity className="w-10 h-10 text-fg/20 mb-3" />
                  <p className="text-fg font-semibold">No usage data</p>
                  <p className="text-fg-muted text-sm mt-1">
                    {user.user_email_id} hasn't made any LLM calls in this period.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Filter drawer — shared component (layers over the modal) */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        theme={theme}
        presets={PRESETS}
        value={{ dateFilter, model, product, context: teamId }}
        selects={[
          {
            key: 'context', label: 'Context', allLabel: 'Personal', lead: true,
            options: availableTeams.map((tm) => ({ value: tm.project_id, label: `Team: ${tm.name || tm.project_id}` })),
          },
          { key: 'model', label: 'Model', allLabel: 'All models', options: availableModels },
          { key: 'product', label: 'Product', allLabel: 'All products', options: availableProducts },
        ]}
        onApply={(next) => {
          setDateFilter(next.dateFilter); setModel(next.model); setProduct(next.product);
          setTeamId(next.context);
        }}
      />
    </>
  );
}
